import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import { Section } from "@cotecna/dynamic-view-engine";
import { ActionElement } from "@cotecna/material";
import { firstValueFrom } from "rxjs";
import { PresetsService } from "src/app/settings/services/presets.service";

import { ADD_INSPECTION, IMPORT_INSPECTIONS } from "../../constants/constants";
import { ImportDialogComponent } from "../../dialogs/import-dialog/import-dialog.component";
import { LoadingComponent } from "../../dialogs/loading/loading.component";
import { LoadingDialogData } from "../../dialogs/loading/loading-dialog-data";
import { ImportInspectionService } from "../../services/import-inspection.service";
import {
  IMPORT_INSPECTIONS_CONTROLS,
  IMPORT_INSPECTIONS_SECTIONS,
} from "./import-inspections-fields";

enum InspectionListState {
  Active = "active",
  Closed = "closed",
}

@Component({
  selector: "im-add-inspection-button",
  templateUrl: "./add-inspection-button.component.html",
  styleUrls: ["./add-inspection-button.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddInspectionButtonComponent implements OnInit {
  @Output()
  public reload: EventEmitter<InspectionListState> = new EventEmitter();

  public speedDialActions = [
    {
      icon: "add_box",
      name: ADD_INSPECTION,
      text: ADD_INSPECTION,
      tooltip: ADD_INSPECTION,
    },
    {
      icon: "publish",
      name: IMPORT_INSPECTIONS,
      text: IMPORT_INSPECTIONS,
      tooltip: IMPORT_INSPECTIONS,
    },
  ] as ActionElement[];

  public isVisible: boolean = false;
  private inspectionPreset;
  private sections: Section[];
  private controls: any[];

  public constructor(
    private dialog: MatDialog,
    private router: Router,
    private presetService: PresetsService,
    private importInspectionService: ImportInspectionService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.inspectionPreset = await this.presetService.getPresetsList();
    this.fillControls();
    this.sections = IMPORT_INSPECTIONS_SECTIONS;
    this.controls = IMPORT_INSPECTIONS_CONTROLS;
    this.isVisible = true;
    this.changeDetectorRef.markForCheck();
  }

  public onFabSpeedDialSelectedAction(actionElement: ActionElement): void {
    const actionName = actionElement.name;
    switch (actionName) {
      case IMPORT_INSPECTIONS:
        this.importInspection();
        break;
      case ADD_INSPECTION:
        this.addInspection();
        break;
    }
  }

  private fillControls(): void {
    IMPORT_INSPECTIONS_CONTROLS.find((c) => c.id == "presetId").options =
      this.inspectionPreset.map((p) => {
        return { id: p.id, name: p.name };
      });
  }

  private async importInspection(): Promise<void> {
    const result = await this.dialog
      .open(ImportDialogComponent, {
        autoFocus: false,
        disableClose: false,
        data: {
          title: IMPORT_INSPECTIONS,
          sections: this.sections,
          controls: this.controls,
        },
        height: "50vh",
        panelClass: ["import-data-dialog"],
      })
      .afterClosed()
      .toPromise();

    if (result) {
      try {
        this.openLoadingDialog("Importing Inspections");
        await this.importInspectionService.importInspection(
          result.data,
          result.file,
        );
        this.reload.emit(InspectionListState.Active);
        this.closeLoadingDialog();
      } catch (err) {
        const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
          data: <ConfirmDialogData>{
            icon: "error",
            okButton: {
              text: "import inspections",
              color: "primary",
            },
            cancelButton: {
              text: "cancel",
            },
            title: "Error importing inspections",
            message: err,
          },
          panelClass: "error-import-items",
          autoFocus: false,
        });
        const confirmDialogResult = await firstValueFrom(
          confirmDialog.afterClosed(),
        );
        if (confirmDialogResult) {
          this.importInspection();
        } else {
          this.closeLoadingDialog();
        }
      }
    }
  }

  private addInspection(): void {
    this.router.navigate(["new-inspection"]);
  }

  private openLoadingDialog(message: string): MatDialogRef<any> {
    return this.dialog.open(LoadingComponent, {
      data: { loadingText: message } as LoadingDialogData,
      autoFocus: false,
      disableClose: false,
    });
  }

  private closeLoadingDialog(): void {
    this.dialog.closeAll();
  }
}
